// Client-side half of the <x-availability-calendar> component
// (resources/views/components/availability-calendar.blade.php).
//
// Every month is already server-rendered, so this script only swaps which one
// shows (the .current class) via each month's nav chevrons, and paints the
// days > 1 range highlight. The chosen date itself is a native radio - the
// checked state and the submitted value never pass through here.
//
// Progressive enhancement: without this script the months stack and the
// radios remain fully usable. Nothing here runs until DOMContentLoaded; the
// .enhanced class is what collapses the stack to one month at a time.

const CALENDAR_SELECTOR = '[data-availability-calendar]';

function getMonths(calendar)
{
    return Array.from(calendar.querySelectorAll('.availability_calendar_month'));
}

function pad(n)
{
    return String(n).padStart(2, '0');
}

// Given a "YYYY-MM-DD" start date, the date strings the stay covers - the
// start plus the following count - 1 days (the Vue MVP's rangeFrom semantics).
function rangeFrom(dateStr, count)
{
    const [y, m, d] = dateStr.split('-').map(Number);
    return Array.from({ length: count }, (_, i) => {
        const date = new Date(y, m - 1, d + i);
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    });
}

// Show the month `delta` places from the current one. The end-stop disabling
// is server-rendered per month (first month's prev, last month's next), so
// out-of-range deltas can't arrive from a click.
function shiftMonth(calendar, delta)
{
    const months = getMonths(calendar);
    const index = months.findIndex(month => month.classList.contains('current'));
    const next = months[index + delta];

    if (!next)
    {
        return;
    }

    months[index].classList.remove('current');
    next.classList.add('current');

    // The clicked chevron just disappeared with its month, so move focus to
    // its counterpart in the newly shown month (or the opposite one at an
    // end-stop) rather than dropping it on <body>.
    const selector = delta < 0 ? '[data-calendar-prev]' : '[data-calendar-next]';
    const fallback = delta < 0 ? '[data-calendar-next]' : '[data-calendar-prev]';
    const target = next.querySelector(selector + ':not(:disabled)') || next.querySelector(fallback + ':not(:disabled)');
    target?.focus();
}

// Paint `className` on the in-range cells (everything the stay covers except
// its start date, which carries its own checked/hover look). Cells are found
// by data-date, so a range crossing into another rendered month highlights
// there too - the user sees it when they navigate across.
function paintRange(calendar, startDate, days, className)
{
    calendar.querySelectorAll('.' + className).forEach(cell => {
        cell.classList.remove(className, 'range_end');
    });

    if (!startDate || days <= 1)
    {
        return;
    }

    const dates = rangeFrom(startDate, days).slice(1);

    dates.forEach((date, i) => {
        const cell = calendar.querySelector(`[data-date="${date}"]`);
        if (cell)
        {
            cell.classList.add(className);
            if (i === dates.length - 1)
            {
                cell.classList.add('range_end');
            }
        }
    });
}

function initCalendar(calendar)
{
    const days = parseInt(calendar.dataset.days || '1', 10);

    calendar.addEventListener('click', (event) => {
        if (event.target.closest('[data-calendar-prev]'))
        {
            shiftMonth(calendar, -1);
        }
        else if (event.target.closest('[data-calendar-next]'))
        {
            shiftMonth(calendar, 1);
        }
    });

    // Committed range - repainted whenever the checked radio changes.
    calendar.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"]'))
        {
            paintRange(calendar, event.target.value, days, 'in_range');
        }
    });

    // Hover preview of the range a click would commit, available days only
    // (only they wrap a label). Cleared on leaving the grid.
    if (days > 1)
    {
        calendar.addEventListener('mouseover', (event) => {
            const cell = event.target.closest('[data-date]');
            if (cell && cell.querySelector('label'))
            {
                paintRange(calendar, cell.dataset.date, days, 'preview');
            }
        });
        calendar.querySelectorAll('.availability_calendar_grid').forEach(grid => {
            grid.addEventListener('mouseleave', () => paintRange(calendar, null, days, 'preview'));
        });
    }

    // A month to show is server-set (the selected date's month, else the
    // first); the guard only covers a malformed selected month key.
    if (!calendar.querySelector('.availability_calendar_month.current'))
    {
        getMonths(calendar)[0]?.classList.add('current');
    }

    calendar.classList.add('enhanced');
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(CALENDAR_SELECTOR).forEach(initCalendar);
});
